import React, { useEffect, useRef, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import Hls from "hls.js";
import useAuthStore from "@/stores/user/authStore";
import { BellIcon, Loader2 } from "lucide-react";
import api from "@/config/axiosConfig";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import logger from "@/utils/logger";

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const videoRef = useRef<HTMLVideoElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [type, setType] = useState<"success" | "error" | "warning">("success");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [modalOpen, setIsModalOpen] = useState(false);
  const videoUrl =
    "https://customer-zm6tqrwkxojntyc8.cloudflarestream.com/1f580192e7aeb418dbb571a85da8e02e/manifest/video.m3u8";

  const emailDomains = [
    "gmail.com",
    "hotmail.com",
    "yahoo.com",
    "outlook.com",
    "icloud.com",
  ];

  const isComingSoon = import.meta.env.VITE_COMING_SOON === "true";

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (Hls.isSupported()) {
      const hls = new Hls({
        startLevel: -1,
        capLevelToPlayerSize: false,
        maxBufferLength: 30,
        maxLoadingDelay: 2,
      });

      hls.loadSource(videoUrl);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().catch((error) => {
          logger.log("Auto-play was prevented:", error);
        });
      });

      return () => {
        hls.destroy();
      };
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = videoUrl;
      video.addEventListener("loadedmetadata", () => {
        video.play().catch((error) => {
          logger.log("Auto-play was prevented:", error);
        });
      });
    }
  }, []);

  const handleGetStarted = useCallback(() => {
    if (user) {
      navigate("/lodge");
    } else {
      navigate("/signup");
    }
  }, [user, navigate]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    const atIndex = e.target.value.lastIndexOf("@");
    if (atIndex !== -1 && !e.target.value.includes(".", atIndex)) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleDomainClick = (domain: string) => {
    if (!email.includes("@")) {
      setEmail(`${email}@${domain}`);
    } else {
      const atIndex = email.lastIndexOf("@");
      const newEmail = email.slice(0, atIndex + 1) + domain;
      setEmail(newEmail);
    }
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const isValidEmail = (email: string): boolean => {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (email === "") {
      setMessage("Please enter your email");
      setType("error");
      setIsModalOpen(true);
      return;
    }

    if (!isValidEmail(email)) {
      setMessage("Please enter a valid email address");
      setType("error");
      setIsModalOpen(true);
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.notify.post("", { email });

      if (response.data.status === "success") {
        setMessage(
          "We'll send you exclusive updates and early access when we launch."
        );
        setType("success");
        setEmail("");
      } else if (response.data.status === "existing") {
        setMessage(
          "This email is already registered. We'll keep you updated on our progress."
        );
        setType("warning");
      } else {
        setMessage(
          "Oops! Something went wrong with your request. Please try again later."
        );
        setType("error");
      }
    } catch (error: any) {
      setMessage(
        error.response?.data?.message ||
          "We couldn't process your request. Please check your connection and try again."
      );
      setType("error");
    } finally {
      setIsLoading(false);
      setIsModalOpen(true);
    }
  };

  return (
    <div className="relative w-full min-h-screen flex flex-col overflow-hidden">
      <div className="absolute inset-0 bg-black">
        <video
          ref={videoRef}
          className="opacity-80 absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
          muted
          playsInline
          loop
        />
      </div>

      <Header
        showAboutButton={true}
        showContactButton={isComingSoon}
        showGetStartedButton={true}
        isComingSoon={isComingSoon}
      />

      <main className="relative z-10 flex-grow flex items-center 2xl:mb-16 justify-center px-4">
        <div className="text-center text-white">
          <h1 className="text-3xl sm:text-4xl font-medium mb-3 sm:mb-4">
            Travel More. Plan Less.
          </h1>
          <p className="text-base sm:text-lg font-light mb-4 sm:mb-5">
            Designed to be Shared
          </p>

          {isComingSoon ? (
            <form
              onSubmit={handleSubmit}
              className="max-w-md mx-auto px-4 sm:px-0"
            >
              <div className="relative">
                <input
                  ref={inputRef}
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder="Enter your email for early access"
                  className="w-full px-4 sm:px-6 py-2.5 sm:py-3 rounded-2xl bg-white/5 backdrop-blur-sm text-white
                    border-2 border-white/20 focus:border-white/50 focus:outline-none
                    placeholder:text-white/50 font-light text-sm sm:text-base"
                />
                <button
                  type="submit"
                  disabled={isLoading}
                  className="absolute right-2 top-1/2 -translate-y-1/2
                    bg-white/10 hover:bg-white/20 text-white px-3 sm:px-4 py-1 sm:py-1.5 rounded-lg sm:rounded-xl
                    transition-all duration-300 disabled:opacity-50"
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
                  ) : (
                    <BellIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                  )}
                </button>

                {showSuggestions && (
                  <div className="absolute w-full h-[200px] overflow-y-auto p-2 mt-2 bg-white/10 backdrop-blur-md rounded-xl transition-opacity duration-300 ease-in-out">
                    {emailDomains.map((domain) => (
                      <button
                        key={domain}
                        type="button"
                        onClick={() => handleDomainClick(domain)}
                        className="w-full px-4 py-3 text-left font-light text-white/90 hover:text-white hover:bg-white/10 rounded-xl transition-all duration-300 ease-in-out text-sm sm:text-base"
                      >
                        {domain}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {/* Legal Statement */}
              <p className="mt-3 sm:mt-4 text-xs text-white/50 text-center px-2 font-light">
                By clicking{" "}
                <span className="sm:hidden">
                  <BellIcon className="h-3 w-3 inline" />
                </span>
                <span className="hidden sm:inline">"Notify Me"</span>, you agree
                to receive emails about Waytora's launch, updates, and
                promotional offers.
              </p>
            </form>
          ) : (
            <button
              onClick={() => navigate("/new-trip")}
              className="group relative bg-transparent px-4 sm:px-5 py-3 text-sm sm:text-lg 2xl:text-xl 2xl:px-8 2xl:py-4 font-medium text-white 
                overflow-hidden rounded-lg border border-white/30 backdrop-blur-sm
                hover:border-white/50 hover:scale-105 transition-all duration-300 ease-out"
              aria-label="Create Your Trip"
            >
              <span className="relative z-10">Create Your Trip</span>
              <div
                className="absolute inset-0 h-full w-full translate-y-full bg-white/10
                group-hover:translate-y-0 transition-transform duration-300 ease-in-out"
              ></div>
            </button>
          )}
        </div>
      </main>

      <Footer />

      {modalOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 backdrop-blur-sm animate-fadeIn p-4">
          <div className="bg-black/40 border border-white/20 p-6 sm:p-8 rounded-2xl w-full max-w-md mx-4 shadow-2xl transform transition-all duration-300 animate-slideUp">
            <div className="flex items-center justify-center mb-4">
              {type === "success" && (
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-2">
                  <svg
                    width="24"
                    height="24"
                    className="sm:w-8 sm:h-8"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.75 12.75L10 15.25L16.25 8.75"
                      stroke="#a7f3d0"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}
              {type === "error" && (
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-red-500/20 rounded-full flex items-center justify-center mb-2">
                  <svg
                    width="24"
                    height="24"
                    className="sm:w-8 sm:h-8"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                      stroke="#fca5a5"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              )}
              {type === "warning" && (
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mb-2">
                  <svg
                    width="24"
                    height="24"
                    className="sm:w-8 sm:h-8"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 9V13M12 17H12.01M6.41209 17.5904L2.3186 9.27098C1.70296 8.02204 2.63629 6.5 4.05321 6.5H19.9468C21.3637 6.5 22.297 8.02204 21.6814 9.27098L17.5879 17.5904C16.9724 18.8393 15.2952 18.8393 14.6796 17.5904L12 12.5L9.32045 17.5904C8.70481 18.8393 7.02773 18.8393 6.41209 17.5904Z"
                      stroke="#fde68a"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              )}
            </div>

            <h3
              className={`text-lg sm:text-xl font-medium text-center mb-2 ${
                type === "error"
                  ? "text-red-200"
                  : type === "warning"
                  ? "text-yellow-200"
                  : "text-green-200"
              }`}
            >
              {type === "success"
                ? "You're on the list!"
                : type === "warning"
                ? "Already registered"
                : "Something went wrong"}
            </h3>

            <p className="text-white/80 text-center mb-4 sm:mb-6 text-sm sm:text-base">
              {type === "success"
                ? "Thank you for your interest! We'll send you exclusive updates and early access when we launch."
                : type === "warning"
                ? "This email is already registered. We'll keep you updated on our progress."
                : message}
            </p>

            <button
              onClick={() => setIsModalOpen(false)}
              className="w-full bg-white/10 hover:bg-white/20 text-white py-2.5 sm:py-3 rounded-xl transition-all duration-300 
              border border-white/10 hover:border-white/30 font-medium hover:scale-[1.02] text-sm sm:text-base"
            >
              {type === "success"
                ? "Can't wait!"
                : type === "warning"
                ? "Got it"
                : "Try Again"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
